<template>
  <div class="view-wrapper d-flex flex-column"
    style="height: 100vh; overflow: hidden; outline: none; position: fixed; width: 100%;" tabindex="0"
    @keydown.stop="handleKeyDown" ref="mainWrapper">
    <div v-if="navigationMode === '2d' && $route.query['log-panel'] === 'true'"
      style="position:fixed;bottom:0;left:0;z-index:9999;background:rgba(0,0,0,0.75);color:#0f0;font-family:monospace;font-size:11px;padding:6px 10px;max-width:320px;pointer-events:none;">
      <div style="color:yellow;font-weight:bold;">v16 - stale-reset</div>
      <div v-for="(line, i) in debugLogs" :key="i">{{ line }}</div>
      <div style="color:#888;">mode:{{ navigationMode }} fi:{{ focusedIndex }}</div>
    </div>

    <audio ref="clapper_audio" style="display:none" preload="auto">
      <source :src="clapperSound" type="audio/wav">
    </audio>
    <audio ref="active_audio" style="display:none" preload="auto">
      <source :src="activeSound" type="audio/wav">
    </audio>
    <audio ref="rest_audio" style="display:none" preload="auto">
      <source :src="restSound" type="audio/wav">
    </audio>
    <audio ref="end_audio" style="display:none" preload="auto">
      <source :src="endSound" type="audio/wav">
    </audio>
    <audio ref="click_audio" style="display:none" preload="auto">
      <source :src="clickSound" type="audio/wav">
    </audio>

    <div class="tier-header px-5 d-flex align-center flex-shrink-0 border-bottom">
      <v-row align="center" no-gutters>
        <v-col cols="5" class="d-flex align-center">
          <template v-if="currentClass">
            <v-avatar :size="isShortScreen ? '6vh' : '5vw'" class="mr-4 elevation-1"
              v-if="currentClass.professorUser && currentClass.professorUser.userImageSmall">
              <ImageDisplay :src="currentClass.professorUser.userImageSmall" />
            </v-avatar>
            <div class="text-left" style="line-height: 1.1">
              <div class="responsive-header-title font-weight-black primary--text text-truncate">{{ currentClass.name }}
              </div>
              <div class="responsive-header-subtitle grey--text text--darken-4 font-weight-bold">
                {{ currentClass.professorUser ? currentClass.professorUser.displayName : '' }}
              </div>
              <div class="responsive-header-subtitle grey--text font-weight-medium">
                {{ $d(new Date(currentClass.startDate), 'time', $i18n.locale) }} - {{ $d(new Date(currentClass.endDate),
                  'time', $i18n.locale) }}
              </div>
            </div>
          </template>
        </v-col>
        <v-col cols="2" class="d-flex justify-center">
          <v-avatar @click="navigateTo({ name: 'checkin' })" v-if="academyLogo" :size="isShortScreen ? '13vh' : '7.5vw'"
            class="elevation-4">
            <ImageDisplay :src="academyLogo" />
          </v-avatar>
        </v-col>
        <v-col cols="5" class="text-right">
          <span v-if="currentTime" class="responsive-clock font-weight-black">{{ $d(currentTime, 'time', $i18n.locale)
            }}</span>
        </v-col>
      </v-row>
    </div>

    <div class="tier-main flex-grow-1" style="min-height: 0;">
      <v-row class="fill-height ma-0" no-gutters>
        <v-col :cols="!showRightPanel ? '12' : '9'" class="d-flex flex-column pa-1 fill-height transition-all">
          <v-card v-if="currentTimer" flat class="timer-container d-flex flex-column fill-height text-center"
            :style="[progressBackground, { '--logo-url': `url(${academyLogo})` }]">

            <div v-if="!isShortScreen" class="pa-2 flex-shrink-0" style="z-index: 2;">
              <div class="d-flex align-center justify-center" style="position: relative;">
                <v-btn v-if="timers.length > 0 || isCustomTimer" fab small :color="isCustomTimer ? 'error' : 'primary'"
                  :class="['control-fab', { 'is-focused': focusedIndex === 5 }]"
                  @click.stop="isCustomTimer ? clearCustomTimer() : openTimerDialog()" @pointerenter="onPointerEnter(5)"
                  style="position: absolute; left: 0;">
                  <v-icon>{{ isCustomTimer ? 'mdi-timer-cancel-outline' : 'mdi-timer-star-outline' }}</v-icon>
                </v-btn>
                <div class="responsive-timer-title font-weight-black text-uppercase">{{ currentTimer.name || academyName
                  }}
                </div>
              </div>
              <div class="responsive-timer-subtitle grey--text text--darken-3 font-weight-bold">
                <span v-if="started">{{ phaseLabel }}</span><span v-else>&nbsp;</span>
                <span v-if="currentIntervalName"> | {{ currentIntervalName }}</span>
                <span v-if="intervalCounter"> | {{ intervalCounter }}</span>
              </div>
            </div>

            <div class="d-flex flex-column align-center justify-center flex-grow-1"
              style="min-height: 0; z-index: 2; overflow: hidden;">
              <div class="responsive-main-timer font-weight-black">{{ formattedTime }}</div>
            </div>

            <div class="control-section pa-2 flex-shrink-0 d-flex align-center justify-space-between"
              style="z-index: 2;">
              <div class="d-flex align-center">
                <v-btn v-if="!paused && started" @click.stop="toggleTimer" @pointerenter="onPointerEnter(0)" fab
                  :x-large="!isShortScreen" :small="isShortScreen" color="yellow"
                  :class="['control-fab', { 'is-focused': focusedIndex === 0 }]"><v-icon>mdi-pause</v-icon></v-btn>
                <v-btn v-else @click.stop="toggleTimer" @pointerenter="onPointerEnter(0)" fab :x-large="!isShortScreen"
                  :small="isShortScreen" color="primary"
                  :class="['control-fab', { 'is-focused': focusedIndex === 0 }]"><v-icon>{{ paused ?
                    'mdi-play-outline' :
                    'mdi-play' }}</v-icon></v-btn>
                <v-btn :disabled="!started" @click.stop="stopTimer" @pointerenter="onPointerEnter(1)" fab
                  :x-large="!isShortScreen" :small="isShortScreen" color="error"
                  :class="['control-fab ml-2', { 'is-focused': focusedIndex === 1 }]"><v-icon>mdi-stop</v-icon></v-btn>
              </div>

              <div class="d-flex align-center">
                <template v-if="isCustomTimer">
                  <div class="text-center mx-1">
                    <div class="responsive-editor-label font-weight-black mb-1" v-if="!isShortScreen">INTERVAL</div>
                    <div class="d-flex align-center">
                      <v-btn fab small :disabled="!canGoPrev"
                        :class="['control-fab', { 'is-focused': focusedIndex === 2 }]"
                        @click.stop="goToInterval('prev')"
                        @pointerenter="onPointerEnter(2)"><v-icon>mdi-chevron-left</v-icon></v-btn>
                      <span class="mx-2 font-weight-black responsive-timer-title">{{ intervalIndex + 1 }} / {{
                        currentTimer.intervals.length }}</span>
                      <v-btn fab small :disabled="!canGoNext"
                        :class="['control-fab', { 'is-focused': focusedIndex === 3 }]"
                        @click.stop="goToInterval('next')"
                        @pointerenter="onPointerEnter(3)"><v-icon>mdi-chevron-right</v-icon></v-btn>
                    </div>
                  </div>
                </template>
                <template v-else>
                  <div class="text-center mx-1" v-for="(type, idx) in ['active', 'rest']" :key="type">
                    <div class="responsive-editor-label font-weight-black mb-1" v-if="!isShortScreen">{{ $t('message.' +
                      type).toUpperCase() }}</div>
                    <div :class="['editor-box', { 'is-focused': focusedIndex === (idx + 2) }]"
                      @pointerenter="onPointerEnter(idx + 2)">
                      <v-btn v-if="isShortScreen" block text x-small class="editor-chevron-large"
                        @click.stop="adjustInterval(type, 'up')"><v-icon
                          :color="type === 'active' ? 'success' : 'error'">mdi-chevron-up</v-icon></v-btn>
                      <v-btn v-else block text class="editor-chevron-large"
                        @click.stop="adjustInterval(type, 'up')"><v-icon
                          :color="type === 'active' ? 'success' : 'error'">mdi-chevron-up</v-icon></v-btn>
                      <input type="text" class="custom-html-input"
                        :value="type === 'active' ? editableActiveTime : editableRestTime" readonly tabindex="-1" />
                      <v-btn v-if="isShortScreen" block text x-small class="editor-chevron-large"
                        @click.stop="adjustInterval(type, 'down')"><v-icon
                          :color="type === 'active' ? 'success' : 'error'">mdi-chevron-down</v-icon></v-btn>
                      <v-btn v-else block text class="editor-chevron-large"
                        @click.stop="adjustInterval(type, 'down')"><v-icon
                          :color="type === 'active' ? 'success' : 'error'">mdi-chevron-down</v-icon></v-btn>
                    </div>
                  </div>
                </template>
                <div class="text-center mx-1">
                  <div class="responsive-editor-label font-weight-black mb-1" v-if="!isShortScreen">{{
                    $t('message.round').toUpperCase() }}</div>
                  <div :class="['round-editor-box', { 'is-focused': !isCustomTimer && focusedIndex === 4 }]"
                    @pointerenter="onPointerEnter(4)">
                    <template v-if="!isCustomTimer">
                      <v-btn v-if="isShortScreen" block text x-small class="editor-chevron-large"
                        @click.stop="adjustInterval('round', 'up')"><v-icon>mdi-chevron-up</v-icon></v-btn>
                      <v-btn v-else block text class="editor-chevron-large"
                        @click.stop="adjustInterval('round', 'up')"><v-icon>mdi-chevron-up</v-icon></v-btn>
                    </template>
                    <input type="text" class="custom-html-input" :value="roundDisplay" readonly tabindex="-1" />
                    <template v-if="!isCustomTimer">
                      <v-btn v-if="isShortScreen" block text x-small class="editor-chevron-large"
                        @click.stop="adjustInterval('round', 'down')"><v-icon>mdi-chevron-down</v-icon></v-btn>
                      <v-btn v-else block text class="editor-chevron-large"
                        @click.stop="adjustInterval('round', 'down')"><v-icon>mdi-chevron-down</v-icon></v-btn>
                    </template>
                  </div>
                </div>
              </div>
            </div>
          </v-card>
        </v-col>

        <transition name="slide-fade">

          <v-col cols="3" class="d-flex flex-column pa-1 fill-height" v-if="showRightPanel">
            <v-card outlined flat class="d-flex flex-column fill-height" style="overflow: hidden;">
              <div class="pa-1 flex-shrink-0 border-bottom text-center" v-if="currentClass">
                <div class="responsive-sidebar-header text-uppercase"><b>{{ checkins.length }}</b> IN CLASS</div>
              </div>
              <div v-else-if="nextClass && !checkins.length">
                <v-card flat>
                  <v-card-text class="text-center">
                    <div class="text-h5"><b>{{ $t('message.next_class') }}:</b></div>
                    <div class="text-h5 mb-2">{{ nextClass.name }}</div>
                    <div v-if="nextClass.professorUser">
                      <v-avatar size="60" v-if="nextClass.professorUser.userImageSmall">
                        <ImageDisplay :src="nextClass.professorUser.userImageSmall" />
                      </v-avatar>
                      <div class="text-h6">{{ nextClass.professorUser.displayName }}</div>
                    </div>
                    <div class="text-h6" v-if="nextClass.startDate">@ {{ $d(new Date(nextClass.startDate), 'time',
                      $i18n.locale) }}</div>
                  </v-card-text>
                </v-card>
              </div>
              <div v-else-if="nextClass">
                <v-card-text class="text-center">
                  <div class="text-h5"><b>{{ $t('message.ready_for') }}:</b></div>
                  <div class="text-h5">{{ nextClass.name }}</div>
                </v-card-text>
              </div>

              <div class="checkin-scroll-area flex-grow-1" ref="checkinContainer" style="overflow-y: auto;">
                <div :class="[compactModeCheckinDisplay ? 'avatar-grid pa-2' : 'full-cards-list pa-3']"
                  :style="compactModeCheckinDisplay ? { '--avatar-cols': avatarColumns } : {}">
                  <template v-for="(i, index) in checkins">
                    <v-card v-if="!compactModeCheckinDisplay" outlined class="checkin-column-item"
                      :key="'checkin_c_' + (i._id || index)">
                      <div class="d-flex align-center pa-1" style="height: 100%;">
                        <v-badge v-if="i.isBirthday" overlap top left color="pink" icon="mdi-cake-variant" class="mr-2">
                          <v-avatar size="40" class="initials-avatar">
                            <ImageDisplay v-if="i.image" :src="i.image" />
                            <span v-else>{{ getInitials(i.userName) }}</span>
                          </v-avatar>
                        </v-badge>
                        <v-avatar v-else size="40" class="mr-2 initials-avatar">
                          <ImageDisplay v-if="i.image" :src="i.image" />
                          <span v-else>{{ getInitials(i.userName) }}</span>
                        </v-avatar>

                        <div class="d-flex flex-column justify-center align-start text-left text-truncate"
                          style="flex: 1; min-width: 0;">
                          <div class="responsive-sidebar-name text-truncate"
                            style="width: 100%; font-size: 0.9rem; line-height: 1.2;">
                            {{ i.userName }}
                          </div>

                          <div class="d-flex align-center" style="height: 14px;">
                            <belt v-if="!i.notBjjUser" :size="8" :belt="i.belt" :show-text="false" />
                          </div>

                          <div v-if="i.isBirthday || i.isProfessor || i.isAssistantProfessor" class="mt-n1">
                            <div v-if="i.isBirthday" class="pink--text" style="font-size: 0.7rem; font-weight: bold;">
                              {{ $t('message.happy_birthday') }}!
                            </div>
                            <div v-else-if="i.isProfessor || i.isAssistantProfessor" class="primary--text"
                              style="font-size: 0.7rem; font-weight: bold;">
                              <v-icon color="primary" size="10">mdi-police-badge-outline</v-icon> {{
                                $t('message.professor') }}
                            </div>
                          </div>
                        </div>
                      </div>
                    </v-card>

                    <div v-else class="avatar-item-wrapper"
                      :style="compactModeCheckinDisplay ? { height: (dynamicAvatarSize + 8) + 'px' } : {}"
                      :key="'checkin_a_' + (i._id || index)">
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                          <v-badge v-bind="attrs" v-on="on" v-if="i.isBirthday" overlap top left color="pink"
                            icon="mdi-cake-variant">
                            <v-avatar :size="dynamicAvatarSize" class="initials-avatar border-white elevation-1">
                              <ImageDisplay v-if="i.image" :src="i.image" />
                              <span v-else>{{ getInitials(i.userName) }}</span>
                            </v-avatar>
                          </v-badge>
                          <v-badge v-bind="attrs" v-on="on" v-else-if="i.isProfessor || i.isAssistantProfessor" overlap
                            top left color="primary" icon="mdi-police-badge-outline">
                            <v-avatar :size="dynamicAvatarSize" class="initials-avatar border-white elevation-1">
                              <ImageDisplay v-if="i.image" :src="i.image" />
                              <span v-else>{{ getInitials(i.userName) }}</span>
                            </v-avatar>
                          </v-badge>
                          <v-avatar v-bind="attrs" v-on="on" v-else :size="dynamicAvatarSize"
                            class="initials-avatar border-white elevation-1">
                            <ImageDisplay v-if="i.image" :src="i.image" />
                            <span v-else>{{ getInitials(i.userName) }}</span>
                          </v-avatar>
                        </template>
                        <span>{{ i.userName }}</span>
                      </v-tooltip>
                    </div>
                  </template>
                </div>
              </div>
              <v-btn v-if="currentClass" fab outlined color="primary" class="floating-info-btn"
                @click="checkinDialog = true">
                <v-icon size="35">mdi-map-marker-check-outline</v-icon>
              </v-btn>
            </v-card>
          </v-col>
        </transition>
      </v-row>
    </div>

    <div class="tier-footer px-5 d-flex align-center justify-space-between flex-shrink-0 border-top bg-white">
      <div class="left-content d-flex align-center fill-height">
        <v-chip color="secondary" label class="responsive-footer-chip" v-if="nextClass">
          <span class="chip-content">
            <b>{{ $t('message.next_class') }}:</b> {{ nextClass.name }}
            <span class="ml-2 font-weight-regular" v-if="nextClass.professorUser">w/ {{
              nextClass.professorUser.displayName
              }}</span>
            <span class="ml-2 font-weight-regular" v-if="nextClass.startDate">@ {{ $d(new Date(nextClass.startDate),
              'time',
              $i18n.locale) }}</span>
          </span>
        </v-chip>
      </div>
      <div class="right-content d-flex align-center fill-height">
        <span class="mr-3 font-weight-bold responsive-brand-text">BJJLINK</span>
        <div class="responsive-footer-logo"><v-img
            src="https://bjjlink-pub.s3.us-west-2.amazonaws.com/logos/bjjlink-2025-black.png" contain></v-img></div>
      </div>
    </div>

    <div v-if="showIntroDialog" class="intro-overlay">
      <div class="intro-countdown font-weight-black">{{ introCountdown }}</div>
      <div v-if="currentTimer.intervals[intervalIndex].name" class="intro-interval-name font-weight-black">
        {{ currentTimer.intervals[intervalIndex].name }}
      </div>
      <div class="intro-text">{{ currentTimer.intervals[intervalIndex].intro.text }}</div>
      <div class="intro-hint">{{ $t('message.press_to_skip') }}</div>
    </div>

    <v-dialog v-model="checkinDialog" width="60%" content-class="bottom-right-dialog"
      transition="dialog-bottom-transition">
      <v-card outlined>
        <v-card-text class="mt-2 mb-0">
          <v-autocomplete hide-details ref="members_autocomplete" v-model="selectedMember" :items="members"
            :loading="loadingMembers" outlined clearable
            :menu-props="{ closeOnContentClick: true, offsetY: true, top: true }" style="font-size:large"
            item-text="displayName" item-value="_id" :label="$t('message.type_name')" height="70">
            <template v-slot:item="data">
              <v-list-item @click="selectMember(data.item)">
                <v-list-item-avatar class="initials-avatar">
                  <ImageDisplay v-if="data.item.userImageSmall" :src="data.item.userImageSmall" /><span v-else>{{
                    getInitials(data.item.displayName) }}</span>
                </v-list-item-avatar>
                <v-list-item-content><v-list-item-title class="text-h6">{{ data.item.displayName
                    }}</v-list-item-title></v-list-item-content>
              </v-list-item>
            </template>
          </v-autocomplete>
        </v-card-text>
        <v-card-actions class="pa-4"><v-spacer></v-spacer>
          <v-btn elevation="0" x-large color="secondary" @click="checkinDialog = false; checkinUserName = null">{{
            $t('message.cancel') }}</v-btn>
          <v-spacer></v-spacer>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showTimerSelectDialog" width="400" content-class="bottom-right-dialog"
      transition="dialog-bottom-transition" @keydown.stop="handleKeyDown">
      <v-card outlined>
        <v-card-title class="text-h6">{{ $t('message.select_timer') }}</v-card-title>
        <v-list>
          <v-list-item v-for="(timer, tIdx) in timers" :key="tIdx" @click="loadTimer(timer)"
            :class="{ 'timer-dialog-focused': dialogFocusedIndex === tIdx }" style="cursor: pointer;">
            <v-list-item-avatar>
              <v-icon :color="timer.cla ? 'accent' : 'accent'">
                {{ timer.cla ? 'mdi-gamepad-variant-outline' : 'mdi-timer-cog-outline' }}
              </v-icon>
            </v-list-item-avatar>
            <v-list-item-content>
              <v-list-item-title class="font-weight-bold">{{ timer.name }}</v-list-item-title>
              <v-list-item-subtitle>{{ timer.intervals.length }} interval{{ timer.intervals.length !== 1 ? 's' : ''
                }}</v-list-item-subtitle>
            </v-list-item-content>
            <v-list-item-action>
              <v-icon color="primary">mdi-chevron-right</v-icon>
            </v-list-item-action>
          </v-list-item>
        </v-list>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text :class="{ 'timer-dialog-focused': dialogFocusedIndex === timers.length }"
            @click="showTimerSelectDialog = false">{{ $t('message.cancel') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import Belt from '@/components/displays/BeltDisplay.vue';
import CountryFlag from '@/components/displays/CountryFlagDisplay.vue';
import Api from "@/services/Api";

export default {
  name: 'FullScreenCheckin',
  data() {
    return {
      navigationMode: this.$route.query.navigation === '2d' ? '2d' : 'default',
      debugLogs: [],
      focusedIndex: 0, checkins: [], lastCheckinCount: -1, members: [], academyLogo: null, academyName: null, currentClass: null, nextClass: null,
      currentTime: new Date(), compactModeCheckinDisplay: false, avatarColumns: 1, dynamicAvatarSize: 60, isShortScreen: false, windowHeight: 0,
      checkinDialog: false, checkinUserName: null, selectedMember: null, loadingMembers: false, checkingIn: false, checkinUserId: null, silenceAudio: null,
      timerId: null, started: false, paused: false, isResting: false, currentRound: 1, roundProgress: 0, backgroundColor: this.themeColor('success'), backgroundPercent: 0, totalTimeInSeconds: 0, intervalIndex: 0,
      classLocation: this.$route.query.location,
      widgetMode: this.$route.query.mode, currentTimer: { name: "", intervals: [{ rest: 60, active: 300, rounds: null }] },
      clickSound: this.assets('sounds/timer_click.wav'), activeSound: this.assets('sounds/timer_opening_bell.wav'),
      restSound: this.assets('sounds/timer_closing_bell.wav'), endSound: this.assets('sounds/end.wav'), clapperSound: this.assets('sounds/timer_clapper.wav'),
      showRightPanel: false,
      timers: [], isCustomTimer: false, showTimerSelectDialog: false, dialogFocusedIndex: 0,
      showIntroDialog: false, introCountdown: 0, introTimerId: null,
    }
  },
  components: { Belt, CountryFlag },
  watch: {
    checkins: {
      handler(newVal) {
        if (newVal.length !== this.lastCheckinCount) {
          this.lastCheckinCount = newVal.length;
          // IMPORTANT: Set a long enough timeout for DOM to actually change before we measure
          this.$nextTick(() => {
            setTimeout(() => { this.calculateSidebarLayout(); }, 400);
          });
        }
      },
      deep: true
    },
    showTimerSelectDialog(val) {
      if (!val) this.$nextTick(() => this.$refs.mainWrapper?.focus());
    }
  },
  computed: {
    editableActiveTime() { return this.secondsToDisplay(this.currentTimer.intervals[this.intervalIndex].active); },
    editableRestTime() { return this.secondsToDisplay(this.currentTimer.intervals[this.intervalIndex].rest); },
    formattedTime() { const mins = Math.floor(this.roundProgress / 60); const secs = this.roundProgress % 60; return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`; },
    currentIntervalName() { return this.currentTimer.intervals[this.intervalIndex]?.name || null; },
    intervalCounter() { const total = this.currentTimer.intervals.length; return total > 1 ? `${this.intervalIndex + 1} / ${total}` : null; },
    phaseLabel() { return this.isResting ? this.$t('message.rest').toUpperCase() : this.$t('message.active').toUpperCase(); },
    progressBackground() {
      return {
        background: `linear-gradient(to top, ${this.backgroundColor} 0%, ${this.backgroundColor} ${this.backgroundPercent}%, transparent ${this.backgroundPercent + 0.1}%)`,
        transition: this.roundProgress === this.totalTimeInSeconds ? 'none' : 'background 0.3s ease'
      };
    },
    canGoPrev() { return this.intervalIndex > 0; },
    canGoNext() { return this.intervalIndex < this.currentTimer.intervals.length - 1; },
    globalCurrentRound() {
      let total = this.currentRound;
      for (let i = 0; i < this.intervalIndex; i++) {
        const r = this.currentTimer.intervals[i].rounds;
        if (r === null) return '∞';
        total += r;
      }
      return total;
    },
    totalRoundsCount() {
      let total = 0;
      for (const iv of this.currentTimer.intervals) {
        if (iv.rounds === null) return '∞';
        total += iv.rounds;
      }
      return total;
    },
    roundDisplay() {
      if (!this.currentTimer) return '0/∞';
      const interval = this.currentTimer.intervals[this.intervalIndex];
      const denominator = !interval || interval.rounds === null ? '∞' : interval.rounds;
      return '' + (this.started ? this.currentRound : 0) + '/' + denominator;
    }
  },
  mounted() {
    if (this.navigationMode === '2d') {
      const style = document.createElement('style');
      style.id = 'fire-tv-cursor-fix';
      style.textContent = 'html, body { overflow: hidden !important; position: fixed !important; width: 100vw !important; height: 100vh !important; } * { cursor: none !important; }';
      document.head.appendChild(style);

      this.debugLog('[init] gp:' + (!!navigator.getGamepads));

      // Always update lastX/lastY on every event so direction is always computed
      // from consecutive events — never from a stale baseline accumulated during cooldown.
      // Cooldown only gates firing, not tracking.
      this._navLastX = null;
      this._navLastY = null;
      this._navCooldown = false;
      this._navStaleReset = null;
      this._navMoveHandler = (/** @type {MouseEvent} */ e) => {
        const prevX = this._navLastX;
        const prevY = this._navLastY;
        this._navLastX = e.clientX;
        this._navLastY = e.clientY;
        // Cancel stale reset — cursor is moving, not stuck
        if (this._navStaleReset) { clearTimeout(this._navStaleReset); this._navStaleReset = null; }
        if (this._navCooldown) return;
        if (prevX === null) return;
        const dx = e.clientX - prevX;
        const dy = e.clientY - prevY;
        if (Math.abs(dx) < 2 && Math.abs(dy) < 2) return;
        const key = Math.abs(dx) >= Math.abs(dy) ? (dx > 0 ? 'ArrowRight' : 'ArrowLeft') : (dy > 0 ? 'ArrowDown' : 'ArrowUp');
        this.debugLog('[mv] ' + key + ' dx:' + Math.round(dx));
        this._navCooldown = true;
        setTimeout(() => {
          this._navCooldown = false;
          // If cursor goes quiet for 400ms after cooldown (stuck at wall), reset baseline
          this._navStaleReset = setTimeout(() => {
            this._navLastX = null;
            this._navLastY = null;
            this._navStaleReset = null;
          }, 400);
        }, 200);
        this.handleKeyDown({ key, keyCode: { ArrowRight: 39, ArrowLeft: 37, ArrowDown: 40, ArrowUp: 38 }[key], preventDefault: () => { } });
      };
      window.addEventListener('mousemove', this._navMoveHandler);

      // Intercept center-button clicks so they fire executeAction() on the focused button
      // rather than whatever element is physically under the cursor
      this._navClickHandler = (/** @type {MouseEvent} */ e) => {
        e.preventDefault();
        e.stopPropagation();
        this.debugLog('[click] fi:' + this.focusedIndex);
        this.executeAction();
      };
      document.addEventListener('click', this._navClickHandler, true);

      // Gamepad API polling — Fire TV remote may expose as a gamepad
      this._gpLastKey = null;
      this._gpInterval = setInterval(() => {
        const gamepads = navigator.getGamepads ? navigator.getGamepads() : [];
        let anyConnected = false;
        for (const gp of gamepads) {
          if (!gp) continue;
          anyConnected = true;
          const dpad = [
            { idx: 12, key: 'ArrowUp' }, { idx: 13, key: 'ArrowDown' },
            { idx: 14, key: 'ArrowLeft' }, { idx: 15, key: 'ArrowRight' },
          ];
          let pressed = null;
          for (const { idx, key } of dpad) {
            if (gp.buttons[idx] && gp.buttons[idx].pressed) { pressed = key; break; }
          }
          if (pressed && pressed !== this._gpLastKey) {
            this._gpLastKey = pressed;
            this.debugLog('[gp] ' + pressed);
            this.handleKeyDown({ key: pressed, keyCode: { ArrowRight: 39, ArrowLeft: 37, ArrowDown: 40, ArrowUp: 38 }[pressed], preventDefault: () => { } });
          } else if (!pressed) {
            this._gpLastKey = null;
          }
        }
        if (!anyConnected && this._gpLoggedNoGp !== true) {
          this._gpLoggedNoGp = true;
          this.debugLog('[gp] no gamepad detected');
        }
      }, 50);
    }
  },
  beforeDestroy() {
    if (this.timerId) {
      clearInterval(this.timerId);
    }
    if (this.introTimerId) {
      clearInterval(this.introTimerId);
    }
    if (this.silenceAudio) {
      this.silenceAudio.pause();
      this.silenceAudio = null;
    }
    const style = document.getElementById('fire-tv-cursor-fix');
    if (style) style.remove();
    if (this._navMoveHandler) window.removeEventListener('mousemove', this._navMoveHandler);
    if (this._navClickHandler) document.removeEventListener('click', this._navClickHandler, true);
    if (this._navStaleReset) clearTimeout(this._navStaleReset);
    if (this._gpInterval) clearInterval(this._gpInterval);
  },
  created() {
    this.getCurrentClassData(); setInterval(() => this.getCurrentClassData(true), 10000); setInterval(() => { this.currentTime = new Date(); }, 1000);
    this.checkScreenSize(); window.addEventListener('resize', this.checkScreenSize); this.getMembers();
    if (this.widgetMode != 'timer')
      this.showRightPanel = true;
  },
  methods: {
    onPointerEnter(idx) {
      if (this.navigationMode === '2d') return;
      this.focusedIndex = idx;
    },
    debugLog(msg) {
      this.debugLogs.push(msg);
      if (this.debugLogs.length > 8) this.debugLogs.shift();
      console.log(msg);
    },
    checkScreenSize() {
      this.windowHeight = window.innerHeight;
      this.isShortScreen = window.innerHeight < 500;
      this.calculateSidebarLayout();
    },
    calculateSidebarLayout() {
      this.$nextTick(() => {
        const container = this.$refs.checkinContainer;
        if (!container) return;

        const checkinCount = this.checkins.length;
        if (checkinCount === 0) {
          this.compactModeCheckinDisplay = false;
          return;
        }

        const availableHeight = container.clientHeight;
        const availableWidth = container.clientWidth;
        if (availableHeight <= 0) return;

        // --- STEP 1: THE DECISION (List or Avatar?) ---
        // We stay in List Mode ONLY if they actually fit.
        const rowHeightEstimate = 60;
        const totalNeededForList = checkinCount * rowHeightEstimate;

        // Switch to compact if the list overflows the container height
        this.compactModeCheckinDisplay = totalNeededForList > (availableHeight + 5);

        // --- STEP 2: THE GRID MATH (Only runs if Compact Mode is true) ---
        if (this.compactModeCheckinDisplay) {
          let bestCols = 2;
          let bestSize = 20; // Start small to find a real fit

          // We test 2, 3, and 4 columns to find the one that fits everyone
          for (let cols = 2; cols <= 4; cols++) {
            const rows = Math.ceil(checkinCount / cols);

            // Calculate the maximum size allowed by WIDTH
            const maxHSize = Math.floor((availableWidth - (cols * 12)) / cols);
            // Calculate the maximum size allowed by HEIGHT
            const maxVSize = Math.floor((availableHeight - (rows * 10)) / rows);

            // The size for THIS column count is the smaller of the two
            let currentSize = Math.min(maxHSize, maxVSize);

            // Cap size at 60px
            if (currentSize > 60) currentSize = 60;

            // Pick the column count that results in the LARGEST possible avatar
            if (currentSize >= bestSize) {
              bestCols = cols;
              bestSize = currentSize;
            }
          }

          this.avatarColumns = bestCols;
          this.dynamicAvatarSize = Math.max(30, bestSize);
        } else {
          // RESET for List Mode
          this.avatarColumns = 1;
          this.dynamicAvatarSize = 60;
        }
      });
    },
    isBirthday(dob) {
      if (!dob || typeof dob !== 'string') return false;
      const bDayMonth = dob.substring(5, 7);
      const bDayDay = dob.substring(8, 10);
      const today = new Date();
      return bDayMonth === (today.getMonth() + 1).toString().padStart(2, '0') &&
        bDayDay === today.getDate().toString().padStart(2, '0');
    },
    getMembers() { this.loadingMembers = true; Api().post("admintablet/academy/" + this.$store.state.tabletManagedAcademy + "/users?source=members").then(res => { this.members = res.data.users; this.loadingMembers = false; }); },
    selectMember(data) {
      this.checkinUserId = data._id;
      this.checkin();
    },
    checkin() {
      this.checkingIn = true; const date = new Date(); const firstHour = new Date(); firstHour.setHours(0); firstHour.setMinutes(0);
      let assistantProfessors = [];
      if (this.currentClass.assistantProfessors) {
        for (const i of this.currentClass.assistantProfessors) assistantProfessors.push(i._id)
      }
      const postData = {
        date: date.toString(),
        firstHour: firstHour,
        userId: this.checkinUserId,
        academyId: this.$store.state.tabletSelectedAcademy,
        professorId: this.currentClass.professorUser ? this.currentClass.professorUser._id : null,
        assistantProfessors: assistantProfessors,
        academyClassId: this.currentClass.academyClassId,
        scheduleClassId: this.currentClass._id,
        className: this.currentClass.name,
        startTime: this.currentClass.start,
      };
      Api().post('admintablet/academy/' + this.$store.state.tabletManagedAcademy + '/checkin/' + this.checkinUserId, postData).then(() => {
        this.checkinUserId = null; this.checkinUserName = null; this.checkingIn = false; this.checkinDialog = false; this.getCurrentClassData();
      }).catch(() => { this.checkingIn = false; });
    },
    getCurrentClassData(isUpdate = false) {
      let url = "admintablet/academy/" + this.$store.state.tabletManagedAcademy + "/checkins-full-screen?now=" + (new Date()).getTime();
      if (this.classLocation && this.classLocation != "")
        url += '&location=' + encodeURI(this.classLocation)
      Api().get(url).then(response => {
        this.academyLogo = response.data.logo;
        this.academyName = response.data.academyName?.toUpperCase();
        this.currentClass = response.data.class;
        this.nextClass = response.data.nextClass;
        if (response.data.checkins) response.data.checkins.forEach(i => i.isBirthday = this.isBirthday(i.dob));
        this.checkins = response.data.checkins || [];
        if (!this.currentClass && this.nextClass && this.nextClass.checkins && this.nextClass.checkins.length)
          this.checkins = this.nextClass.checkins;
        if (!this.started && !isUpdate) { this.roundProgress = this.currentTimer.intervals[0].active; this.totalTimeInSeconds = this.roundProgress; }
        if (response.data.timers) this.timers = response.data.timers;
        if (this.widgetMode != 'timer' && !this.currentClass && !this.nextClass) {
          this.showRightPanel = false;
        } else if (this.widgetMode != 'timer') {
          this.showRightPanel = true;
        }
      });
    },
    loadTimer(timer) {
      this.stopTimer();
      this.currentTimer = JSON.parse(JSON.stringify(timer));
      this.intervalIndex = 0;
      this.isCustomTimer = true;
      this.showTimerSelectDialog = false;
      const iv = this.currentTimer.intervals[0];
      this.roundProgress = iv.active;
      this.totalTimeInSeconds = iv.active;
      this.backgroundColor = this.themeColor('success');
      this.focusedIndex = 0;
    },
    clearCustomTimer() {
      this.stopTimer();
      this.currentTimer = { name: "", intervals: [{ rest: 60, active: 300, rounds: null }] };
      this.intervalIndex = 0;
      this.isCustomTimer = false;
      this.roundProgress = this.currentTimer.intervals[0].active;
      this.totalTimeInSeconds = this.roundProgress;
      this.backgroundColor = this.themeColor('success');
    },
    goToInterval(direction) {
      const newIndex = direction === 'next' ? this.intervalIndex + 1 : this.intervalIndex - 1;
      if (newIndex < 0 || newIndex >= this.currentTimer.intervals.length) return;
      const wasRunning = this.started && !this.paused;
      if (wasRunning) clearInterval(this.timerId);
      clearInterval(this.introTimerId);
      this.introTimerId = null;
      this.showIntroDialog = false;
      this.intervalIndex = newIndex;
      this.currentRound = 1;
      this.isResting = false;
      const iv = this.currentTimer.intervals[newIndex];
      this.roundProgress = iv.active;
      this.totalTimeInSeconds = iv.active;
      this.backgroundPercent = 0;
      this.backgroundColor = this.themeColor('success');
      if (wasRunning) {
        if (!this.showIntroForCurrentInterval()) {
          this.timerId = setInterval(() => this.tick(), 1000);
          this.playSound('active_audio');
        }
      }
    },
    getInitials(name) { if (!name) return ""; const p = name.split(' '); return (p[0][0] + (p[1] ? p[1][0] : '')).toUpperCase(); },
    secondsToDisplay(s) { return `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`; },
    adjustInterval(t, d) {
      if (t == 'round') {
        if (this.currentTimer.intervals[this.intervalIndex]) {
          if (d == 'up') {
            if (this.currentTimer.intervals[this.intervalIndex].rounds === null)
              this.currentTimer.intervals[this.intervalIndex].rounds = 1;
            else
              this.currentTimer.intervals[this.intervalIndex].rounds++;
          } else {
            if (this.currentTimer.intervals[this.intervalIndex].rounds >= 2) {
              this.currentTimer.intervals[this.intervalIndex].rounds--
            } else {
              this.currentTimer.intervals[this.intervalIndex].rounds = null;
            }
          }
        }
      } else {
        this.stopTimer(); const i = this.currentTimer.intervals[this.intervalIndex];
        const step = (t === 'active') ? 15 : 5;
        const min = (t === 'active') ? 15 : 5;
        i[t] = d === 'up' ? i[t] + step : Math.max(min, i[t] - step);
        this.roundProgress = i.active; this.totalTimeInSeconds = i.active;
      }
    },
    toggleTimer() { this.started && !this.paused ? this.pauseTimer() : this.startTimer(); },
    startTimer() {
      const fromPaused = this.paused ? true : false;
      this.started = true;
      this.paused = false;

      // START SILENT ANCHOR: Keeps Bluetooth "Live"
      if (!this.silenceAudio) {
        // This is a 1-second silent WAV file base64
        this.silenceAudio = new Audio("data:audio/wav;base64,UklGRigAAABXQVZFRm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQAAAAA=");
        this.silenceAudio.loop = true;
        this.silenceAudio.volume = 0.01; // Barely active but keeps the driver open
      }
      this.silenceAudio.play().catch(() => { });

      if (!fromPaused && this.showIntroForCurrentInterval()) return;

      this.timerId = setInterval(() => this.tick(), 1000);
      if (!fromPaused) this.playSound('active_audio');
    },
    pauseTimer() { this.paused = true; clearInterval(this.timerId); },
    stopTimer() {
      clearInterval(this.timerId);
      clearInterval(this.introTimerId);
      this.introTimerId = null;
      this.showIntroDialog = false;
      this.started = false;
      this.paused = false;
      this.currentRound = 1;
      this.intervalIndex = 0;

      if (this.silenceAudio) {
        this.silenceAudio.pause();
      }

      this.backgroundPercent = 0;
      this.isResting = false;
      this.backgroundColor = '#4CAF50';
      this.roundProgress = this.currentTimer.intervals[0].active;
      this.totalTimeInSeconds = this.roundProgress;
    },
    tick() {
      if (this.roundProgress > 0) {
        this.roundProgress--;
        this.backgroundPercent = ((this.totalTimeInSeconds - this.roundProgress) / this.totalTimeInSeconds) * 100;

        if (this.roundProgress === 10 && !this.isResting) {
          this.playSound('clapper_audio');
        }
      } else {
        this.handleTransition();
      }
    },
    handleTransition() {
      this.backgroundPercent = 0;
      const interval = this.currentTimer.intervals[this.intervalIndex];

      if (!this.isResting && interval.rest > 0) {
        this.isResting = true;
        this.roundProgress = interval.rest;
        this.totalTimeInSeconds = interval.rest;
        this.backgroundColor = this.themeColor('error');
        this.playSound('rest_audio');
      } else {
        if (interval.rounds !== null) {
          const nextRound = this.currentRound + 1;
          if (nextRound > interval.rounds) {
            const nextIdx = this.intervalIndex + 1;
            if (nextIdx >= this.currentTimer.intervals.length) {
              this.playSound('end_audio');
              this.stopTimer();
              return;
            }
            this.intervalIndex = nextIdx;
            this.currentRound = 1;
            const next = this.currentTimer.intervals[nextIdx];
            this.isResting = false;
            this.roundProgress = next.active;
            this.totalTimeInSeconds = next.active;
            this.backgroundColor = this.themeColor('success');
            clearInterval(this.timerId);
            if (!this.showIntroForCurrentInterval()) {
              this.playSound('active_audio');
              this.timerId = setInterval(() => this.tick(), 1000);
            }
            return;
          }
        }
        this.isResting = false;
        this.roundProgress = interval.active;
        this.totalTimeInSeconds = interval.active;
        this.backgroundColor = this.themeColor('success');
        this.playSound('active_audio');

        if (this.started) this.currentRound++;
      }
    },
    playSound(ref, volume = 1.0) {
      const el = this.$refs[ref];
      if (el) {
        el.volume = volume;
        el.currentTime = 0;
        el.play().catch(() => {
          console.log("Audio play blocked by browser/interaction");
        });
      }
    },
    openTimerDialog() {
      this.dialogFocusedIndex = 0;
      this.showTimerSelectDialog = true;
    },
    showIntroForCurrentInterval() {
      const interval = this.currentTimer.intervals[this.intervalIndex];
      if (!this.currentTimer.cla || !interval?.intro?.active) return false;
      clearInterval(this.introTimerId);
      this.showIntroDialog = true;
      this.introCountdown = interval.intro.duration || 10;
      this.introTimerId = setInterval(() => {
        this.introCountdown--;
        if (this.introCountdown <= 0) this.dismissIntro();
      }, 1000);
      return true;
    },
    dismissIntro() {
      clearInterval(this.introTimerId);
      this.introTimerId = null;
      this.showIntroDialog = false;
      this.playSound('active_audio');
      this.timerId = setInterval(() => this.tick(), 1000);
    },
    handleKeyDown(e) {
      if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) {
        if (this.navigationMode === '2d') this.debugLog('[key] ' + e.key + ' fi:' + this.focusedIndex);
        else console.log('[Timer Nav] key:', e.key, '| navigationMode:', this.navigationMode, '| focusedIndex:', this.focusedIndex);
      }
      if (this.checkinDialog) return;

      if (this.showIntroDialog) {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.dismissIntro(); }
        return;
      }

      const SELECT_IDX = 5;
      const totalButtons = 5;
      if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', ' '].includes(e.key)) e.preventDefault();

      // Handle keyboard navigation inside the timer select dialog
      if (this.showTimerSelectDialog) {
        if (e.key === 'Escape') { this.showTimerSelectDialog = false; return; }
        if (e.key === 'ArrowDown') { this.dialogFocusedIndex = Math.min(this.dialogFocusedIndex + 1, this.timers.length); return; }
        if (e.key === 'ArrowUp') { this.dialogFocusedIndex = Math.max(this.dialogFocusedIndex - 1, 0); return; }
        if (e.key === 'Enter') {
          e.preventDefault();
          if (this.dialogFocusedIndex === this.timers.length) { this.showTimerSelectDialog = false; return; }
          if (this.timers[this.dialogFocusedIndex]) { this.loadTimer(this.timers[this.dialogFocusedIndex]); }
          return;
        }
        return;
      }

      // SELECT_IDX virtual button (timer select/clear)
      if (this.focusedIndex === SELECT_IDX) {
        if (e.key === 'ArrowDown') { this.focusedIndex = 0; return; }
        if (e.keyCode === 13) {
          if (this.isCustomTimer) this.clearCustomTimer();
          else this.openTimerDialog();
          return;
        }
        return;
      }

      // 1. Horizontal Navigation (Right/Left) — cycles 0–4
      if (e.key === 'ArrowRight') {
        this.focusedIndex = (this.focusedIndex + 1) % totalButtons;
      }
      else if (e.key === 'ArrowLeft') {
        this.focusedIndex = (this.focusedIndex + (totalButtons - 1)) % totalButtons;
      }

      // 2. Vertical (Up/Down)
      else if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
        const direction = e.key === 'ArrowUp' ? 'up' : 'down';

        // Up from play button goes to timer select if available
        if (this.focusedIndex === 0 && direction === 'up' && (this.timers.length > 0 || this.isCustomTimer)) {
          this.focusedIndex = SELECT_IDX;
          return;
        }

        if (!this.isCustomTimer) {
          if (this.focusedIndex === 2) this.adjustInterval('active', direction);
          else if (this.focusedIndex === 3) this.adjustInterval('rest', direction);
          else if (this.focusedIndex === 4) this.adjustInterval('round', direction);
        }
      }

      // 3. Execution (Enter Key)
      if (e.keyCode === 13) this.executeAction();
    },
    executeAction() {
      if (this.focusedIndex === 0) this.toggleTimer();
      else if (this.focusedIndex === 1) this.stopTimer();
      else if (this.isCustomTimer && this.focusedIndex === 2) this.goToInterval('prev');
      else if (this.isCustomTimer && this.focusedIndex === 3) this.goToInterval('next');
    }
  }
}
</script>

<style>
.bottom-right-dialog {
  position: fixed !important;
  bottom: 20px !important;
  right: 20px !important;
  margin: 0 !important;
}
</style>
<style scoped>
.view-wrapper {
  height: 100vh;
  width: 100vw;
  overflow: hidden !important;
  position: fixed;
  top: 0;
  left: 0;
}

.tier-header {
  height: 12vh;
  min-height: 85px;
  z-index: 10;
  background: white;
}

.tier-main {
  height: 78vh;
  overflow: hidden;
}

.tier-footer {
  height: 10vh;
  min-height: 75px;
  z-index: 10;
  border-top: 2px solid #eee;
}

.responsive-main-timer {
  /* Lower the height-based size slightly for better clearance */
  font-size: min(32vh, 22vw) !important;
  line-height: 1 !important;
  margin: 0;
  padding: 0;
  /* Flex centering is the key to preventing top-clipping */
  display: flex;
  align-items: center;
  justify-content: center;
  /* This prevents the text from ever being 'higher' than its box */
  height: 100%;
}

.responsive-clock {
  font-size: 6vh !important;
}

.responsive-header-title {
  font-size: clamp(16px, 2.5vw, 36px) !important;
}

.responsive-header-subtitle {
  font-size: clamp(12px, 1.4vw, 20px) !important;
}

.responsive-timer-title {
  font-size: 2.2vh !important;
}

.responsive-brand-text {
  font-size: clamp(16px, 2vw, 32px) !important;
  color: #222;
}

.responsive-footer-logo {
  width: clamp(60px, 8vw, 150px);
}

.responsive-footer-chip {
  font-size: clamp(14px, 1.5vw, 26px) !important;
  height: auto !important;
  padding: 12px 24px !important;
}

.control-section {
  min-height: 110px;
  border-radius: 12px;
}

.editor-box {
  width: clamp(110px, 15vw, 260px) !important;
  background: rgba(0, 0, 0, 0.1) !important;
  border-radius: 10px;
  overflow: hidden;
  border: 2px solid transparent;
}

.round-editor-box {
  width: clamp(80px, 11vw, 220px) !important;
  background: rgba(0, 0, 0, 0.1) !important;
  border-radius: 10px;
  overflow: hidden;
  border: 2px solid transparent;
}

.custom-html-input {
  width: 100%;
  height: 7vh;
  background: white;
  border: none;
  font-size: 4vh;
  font-weight: 900;
  text-align: center;
  outline: none;
}

.is-focused {
  border: 4px solid #1976D2 !important;
  box-shadow: 0 0 15px #1976D2;
}

.control-fab.is-focused {
  border: none !important;
  box-shadow: 0 0 0 3px white, 0 0 0 7px #1976D2, 0 0 20px rgba(25, 118, 210, 0.5) !important;
}

.timer-dialog-focused {
  background: rgba(25, 118, 210, 0.12) !important;
  border-left: 4px solid #1976D2 !important;
}

.intro-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: #000;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px;
  box-sizing: border-box;
}

.intro-countdown {
  color: #fff;
  font-size: 18vh;
  line-height: 1;
  margin-bottom: 8px;
}

.intro-interval-name {
  color: #fff;
  font-size: 4vh;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin-bottom: 36px;
  text-align: center;
}

.intro-text {
  color: rgba(255, 255, 255, 0.82);
  font-size: 2.2vh;
  text-align: center;
  max-width: 900px;
  line-height: 1.75;
  overflow-y: auto;
  max-height: 45vh;
}

.intro-hint {
  position: absolute;
  bottom: 28px;
  color: rgba(255, 255, 255, 0.3);
  font-size: 1.6vh;
  letter-spacing: 0.05em;
}

.checkin-scroll-area {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
}

.initials-avatar {
  /* Ensures the border doesn't push the size beyond the 60px target */
  box-sizing: border-box;
  border: 2px solid white;
}

.responsive-sidebar-name {
  font-weight: 700;
  margin-bottom: 2px;
}

.responsive-sidebar-sub {
  font-size: 0.7vw;
  margin-top: 2px;
}

@media (min-height: 501px) and (max-height: 650px) {
  .responsive-main-timer {
    /* Shrink the font just a bit more in this specific height window */
    font-size: min(35vh, 39vw) !important;
    /* Pull the text slightly down from the top */
    transform: translateY(2vh);
  }
}

@media (max-height: 500px) {
  .tier-header {
    height: 14vh;
    min-height: 65px;
  }

  .tier-footer {
    height: 14vh;
    min-height: 65px;
  }

  .responsive-main-timer {
    font-size: min(60vh, 25vw) !important;
    line-height: 0.75 !important;
    padding-top: 0;
  }
}

.timer-container::before {
  content: "";
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 60vh;
  height: 60vh;
  background-image: var(--logo-url);
  background-size: cover;
  background-position: center;
  opacity: 0.05;
  z-index: 1;
  pointer-events: none;
  border-radius: 50%;
  overflow: hidden;
}

.floating-info-btn {
  position: absolute !important;
  bottom: 10px;
  right: 10px;
  z-index: 99;
  scale: 0.8;
}

.responsive-round-counter {
  /* Original was clamp(14px, 2.5vh, 28px) */
  font-size: clamp(16px, 3.5vh, 36px) !important;
  font-weight: 900;
  text-transform: uppercase;
}

.checkin-column-item {
  width: 100% !important;
  min-height: 56px !important;
  margin-bottom: 4px !important;
  padding: 0 !important;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.avatar-grid {
  display: grid;
  /* Use the columns from JS */
  grid-template-columns: repeat(var(--avatar-cols, 2), 1fr);
  gap: 8px;
  width: 100%;
  justify-items: center;
  align-items: center;
}

.avatar-item-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  /* This ensures the row height matches our JS calculation */
  min-height: min-content;
}

/* Also ensure the list container forces alignment */
.full-cards-list {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  /* Forces children to take full width */
  width: 100%;
}

/* ANIMATIONs*/
/* 1. Timer Column Smooth Resize */
.transition-all {
  transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
}

/* 2. Sidebar Slide Animation */
.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: all 0.5s ease;
}

.slide-fade-enter {
  transform: translateX(100%);
  /* Start off-screen to the right */
  opacity: 0;
}

.slide-fade-leave-to {
  transform: translateX(100%);
  /* Slide out to the right */
  opacity: 0;
}

/* Optional: Prevent layout flickering during transition */
.row {
  flex-wrap: nowrap !important;
}
</style>
